/**
 * Add or edit a journal entry (قيد يومية): lines of debit or credit against
 * chart accounts, in one currency. It saves only when the debits equal the
 * credits - the totals under the lines say how far off it is while typing.
 *
 * Each line's account is picked the way the chart is laid out - the main
 * account, then the sub-account under it, or every sub-account at once
 * (AccountTreePick). Only the accounts the server says can take a line are
 * offered (leaves of the chart; boxes, customers and suppliers move by
 * their own pages). A new
 * line starts with whatever still balances the entry, on the side it is
 * missing from.
 */

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import DialogShell, { FieldGrid } from "./DialogShell";
import { Field, Pick, Section, asOptions } from "./Fields";
import AccountTreePick from "./AccountTreePick";
import { api, problemOf, today, money, docNo, CURRENCIES } from "./api";

const emptyLine = () => ({ account_id: "", debit: "", credit: "", note: "" });

const blank = () => ({
  date: today(),
  currency: "ILS",
  description: "",
  reference: "",
  lines: [emptyLine(), emptyLine()],
});

const num = (v) => Math.round((Number(v) || 0) * 100) / 100;

/** Debit and credit totals of the lines, and what is left to balance. */
export function balanceOf(lines) {
  const debit = num(lines.reduce((t, l) => t + num(l.debit), 0));
  const credit = num(lines.reduce((t, l) => t + num(l.credit), 0));
  return { debit, credit, difference: num(debit - credit) };
}

const LINE_GRID = { xs: "1fr", md: "minmax(0, 3.4fr) 1fr 1fr minmax(0, 1.2fr) 36px" };

function JournalDialog({ open, entry, onClose, onSaved }) {
  const [form, setForm] = useState(blank());
  const [accounts, setAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setForm(
      entry
        ? {
            date: entry.date,
            currency: entry.currency,
            description: entry.description || "",
            reference: entry.reference || "",
            lines: entry.lines.map((l) => ({
              account_id: l.account_id,
              debit: l.debit ? String(l.debit) : "",
              credit: l.credit ? String(l.credit) : "",
              note: l.note || "",
            })),
          }
        : blank()
    );
    api
      .get("/accounts/postable", { for: "journal" })
      .then((rows) => setAccounts(Array.isArray(rows) ? rows : []))
      .catch((e) => setError(problemOf(e, "Could not load the accounts")));
  }, [open, entry]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const setLine = (i, patch) =>
    setForm((f) => ({ ...f, lines: f.lines.map((l, n) => (n === i ? { ...l, ...patch } : l)) }));

  const addLine = () =>
    setForm((f) => {
      // the new line starts with what still balances the entry
      const { difference } = balanceOf(f.lines);
      const line = emptyLine();
      if (difference > 0) line.credit = String(difference);
      if (difference < 0) line.debit = String(-difference);
      return { ...f, lines: [...f.lines, line] };
    });

  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, n) => n !== i) }));

  // accounts switched off are offered only where a line already has them
  const used = new Set(form.lines.map((l) => String(l.account_id)));
  const options = accounts.filter((a) => a.is_active || used.has(String(a.id)));
  const total = balanceOf(form.lines);
  const balanced = total.debit > 0 && total.difference === 0;
  const untyped = !total.debit && !total.credit;
  let tone = { color: "#B23A2E", backgroundColor: "rgba(178,58,46,0.1)" };
  if (balanced) tone = { color: "#2E7D32", backgroundColor: "rgba(46,125,50,0.1)" };
  if (untyped) tone = { color: "rgba(11,27,58,0.55)", backgroundColor: "rgba(11,27,58,0.06)" };
  let verdict = `الفرق · Off by ${money(Math.abs(total.difference))}`;
  if (balanced) verdict = "متوازن ✓ Balanced";
  if (untyped) verdict = "اكتب المبالغ · Type the amounts";

  const save = async () => {
    // a line with neither an account nor an amount is just a spare row
    const lines = form.lines.filter((l) => l.account_id || num(l.debit) || num(l.credit));
    const at = lines.findIndex((l) => !l.account_id || num(l.debit) > 0 === num(l.credit) > 0);
    if (at >= 0)
      return setError(`Line ${at + 1}: pick the account and put the amount in debit or credit`);
    if (lines.length < 2) return setError("An entry needs at least two lines");
    if (!balanced)
      return setError(
        `Debit ${money(total.debit)} and credit ${money(total.credit)} are not equal - ` +
          `the difference is ${money(Math.abs(total.difference))}`
      );

    setSaving(true);
    setError("");
    const body = {
      ...form,
      lines: lines.map((l) => ({
        account_id: Number(l.account_id),
        debit: num(l.debit),
        credit: num(l.credit),
        note: l.note,
      })),
    };
    try {
      if (entry) await api.put(`/journal/${entry.id}`, body);
      else await api.post("/journal", body);
      setSaving(false);
      onSaved();
      onClose();
    } catch (e) {
      setSaving(false);
      setError(problemOf(e, "Could not save the entry"));
    }
    return null;
  };

  const name = "Journal Entry · قيد يومية";

  return (
    <DialogShell
      open={open}
      title={entry ? `${docNo("journal", entry.number)} · ${name}` : `New ${name}`}
      onClose={onClose}
      onSave={save}
      saving={saving}
      error={error}
      maxWidth="lg"
    >
      <Section title="The entry · القيد">
        <FieldGrid columns={3}>
          <Field
            label="Date · التاريخ"
            type="date"
            value={form.date}
            onChange={(e) => set({ date: e.target.value })}
          />
          <Pick
            label="Currency · العملة"
            options={asOptions(CURRENCIES)}
            value={form.currency}
            onChange={(c) => set({ currency: c || "ILS" })}
            getLabel={(c) => c.name}
          />
          <Field
            label="Reference · المرجع"
            placeholder="Optional"
            value={form.reference}
            onChange={(e) => set({ reference: e.target.value })}
          />
        </FieldGrid>
        <Field
          label="Description · البيان"
          placeholder="e.g. Office car bought - شراء سيارة للمكتب"
          value={form.description}
          onChange={(e) => set({ description: e.target.value })}
        />
      </Section>

      <Section title="Lines · الأطراف">
        {form.lines.map((l, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <SuiBox key={i} sx={{ display: "grid", gridTemplateColumns: LINE_GRID, columnGap: 1.5 }}>
            <AccountTreePick
              mainLabel={`Main ${i + 1} · الرئيسي`}
              label={`Account ${i + 1} · الحساب`}
              options={options}
              value={l.account_id}
              onChange={(id) => setLine(i, { account_id: id })}
              noOptionsText="No account - add it on the Chart tab"
            />
            <Field
              label="Debit · مدين"
              type="number"
              value={l.debit}
              onChange={(e) =>
                setLine(i, { debit: e.target.value, credit: e.target.value ? "" : l.credit })
              }
              inputProps={{ min: 0, step: "any", "aria-label": `Debit ${i + 1}` }}
            />
            <Field
              label="Credit · دائن"
              type="number"
              value={l.credit}
              onChange={(e) =>
                setLine(i, { credit: e.target.value, debit: e.target.value ? "" : l.debit })
              }
              inputProps={{ min: 0, step: "any", "aria-label": `Credit ${i + 1}` }}
            />
            <Field
              label="Note · ملاحظة"
              value={l.note}
              onChange={(e) => setLine(i, { note: e.target.value })}
            />
            <IconButton
              aria-label={`Remove line ${i + 1}`}
              disabled={form.lines.length <= 2}
              onClick={() => removeLine(i)}
              sx={{ mt: 2.5, alignSelf: "start" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </SuiBox>
        ))}

        <SuiBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1.5}
          mt={0.5}
        >
          <Button
            variant="outlined"
            color="dark"
            size="small"
            startIcon={<AddIcon />}
            onClick={addLine}
          >
            Add line · سطر
          </Button>
          <SuiBox display="flex" alignItems="center" gap={2.5} flexWrap="wrap">
            <SuiTypography sx={{ fontSize: 14 }}>
              {`Debit · مدين: `}
              <b>{`${money(total.debit)} ${form.currency}`}</b>
            </SuiTypography>
            <SuiTypography sx={{ fontSize: 14 }}>
              {`Credit · دائن: `}
              <b>{`${money(total.credit)} ${form.currency}`}</b>
            </SuiTypography>
            <SuiBox
              component="span"
              aria-label="Balance"
              sx={{
                fontSize: 13,
                fontWeight: 700,
                px: 1.25,
                py: 0.5,
                borderRadius: "8px",
                ...tone,
              }}
            >
              {verdict}
            </SuiBox>
          </SuiBox>
        </SuiBox>
      </Section>
    </DialogShell>
  );
}

JournalDialog.defaultProps = { entry: null };

JournalDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  entry: PropTypes.shape({
    id: PropTypes.number,
    number: PropTypes.number,
    date: PropTypes.string,
    currency: PropTypes.string,
    description: PropTypes.string,
    reference: PropTypes.string,
    lines: PropTypes.arrayOf(PropTypes.object),
  }),
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default JournalDialog;
